import { Button } from "@dash/form";
import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import heic2any from "heic2any";
import { useClientManager } from "@megaron/react-clients";
import type { MessageAttachmentPayloadDto, MessageAttachmentSummaryDto } from "@megaron/support-contracts";
import type { Uuid } from "@megaron/uuid";
import { Document, Page, pdfjs } from "react-pdf";
import { MdDownload } from "react-icons/md";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import { decodeBase64ToUint8Array } from "./supportTicketAttachmentDecode";
import { triggerAttachmentDownload } from "./supportTicketAttachmentTriggerDownload";

if (typeof window !== "undefined") {
  pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
}

type Props = {
  ticketId: Uuid;
  messageId: Uuid;
  summary: MessageAttachmentSummaryDto;
};

export function SupportTicketAttachmentExpanded({ ticketId, messageId, summary }: Props) {
  const theme = useTheme();
  const attachmentQuery = useClientManager("support").getTicketMessageAttachment().useQuery({
    id: ticketId,
    messageId,
    attachmentId: summary.id,
  });

  const payload: MessageAttachmentPayloadDto | undefined = attachmentQuery.data;
  const displayType = payload?.fileType ?? summary.fileType;

  const contentRef = useRef<HTMLDivElement>(null);
  const [pageWidth, setPageWidth] = useState(400);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const sync = () => {
      const w = el.clientWidth;
      setPageWidth(Math.max(160, Math.floor(w) - 2));
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const pdfBytes = useMemo(() => {
    if (!payload || displayType !== "application/pdf") return null;
    return decodeBase64ToUint8Array(payload.contentBase64);
  }, [payload, displayType]);

  useEffect(() => {
    if (!payload || displayType === "application/pdf") return undefined;
    if (!displayType.startsWith("image/")) return undefined;
    let urlToRevoke: string | null = null;
    const run = async () => {
      try {
        setImageError(null);
        const raw = decodeBase64ToUint8Array(payload.contentBase64);
        const blobPart = Uint8Array.from(raw);
        if (displayType === "image/heic") {
          const blob = new Blob([blobPart], { type: "image/heic" });
          const converted = await heic2any({ blob, toType: "image/jpeg" });
          const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
          if (!(jpegBlob instanceof Blob)) return;
          const url = URL.createObjectURL(jpegBlob);
          urlToRevoke = url;
          setImageSrc(url);
          return;
        }
        const blob = new Blob([blobPart], { type: displayType });
        const url = URL.createObjectURL(blob);
        urlToRevoke = url;
        setImageSrc(url);
      } catch {
        setImageError("Nie udało się wyświetlić podglądu.");
        setImageSrc(null);
      }
    };
    void run();
    return () => {
      if (urlToRevoke) URL.revokeObjectURL(urlToRevoke);
    };
  }, [payload, displayType]);

  const isImage = displayType.startsWith("image/");
  const isPdf = displayType === "application/pdf";

  return (
    <div
      css={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        maxHeight: "min(78vh, 820px)",
      }}
    >
      <div
        ref={contentRef}
        css={{
          overflowX: "hidden",
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {payload && (
          <div css={{ marginBottom: "0.75rem" }}>
            <Button
              variant="outline"
              size="small"
              icon={<MdDownload />}
              onClick={() =>
                triggerAttachmentDownload(payload.contentBase64, payload.fileType, payload.fileName)
              }
              css={{ alignSelf: "flex-start" }}
            >
              Pobierz
            </Button>
          </div>
        )}
        {attachmentQuery.isLoading && (
          <div css={{ display: "flex", justifyContent: "center", padding: "3rem" }}>
            <Spinner color={theme.colors.primary} size="32px" />
          </div>
        )}
        {attachmentQuery.isError && (
          <div css={{ color: theme.colors.danger, padding: "1rem" }}>Nie udało się wczytać pliku.</div>
        )}
        {!attachmentQuery.isLoading && isPdf && pdfBytes && (
          <div css={{ display: "flex", justifyContent: "center", width: "100%", minWidth: 0 }}>
            <Document
              file={{ data: pdfBytes }}
              loading={
                <div css={{ padding: "2rem" }}>
                  <Spinner color={theme.colors.primary} size="28px" />
                </div>
              }
            >
              <Page pageNumber={1} width={pageWidth} renderTextLayer={false} renderAnnotationLayer={false} />
            </Document>
          </div>
        )}
        {!attachmentQuery.isLoading && isImage && imageError && (
          <div css={{ color: theme.colors.danger, padding: "1rem" }}>{imageError}</div>
        )}
        {!attachmentQuery.isLoading && isImage && imageSrc && (
          <img
            alt=""
            src={imageSrc}
            css={{
              display: "block",
              maxWidth: "100%",
              height: "auto",
              margin: "0 auto",
            }}
          />
        )}
      </div>
    </div>
  );
}
